/**
 * Authentication utility functions
 */

export interface StoredUser {
  userId?: number;
  id?: number;  // For backward compatibility
  username: string;
  email: string;
  firstName: string;
  lastName: string;
  role: string;
}

/**
 * Check if user is authenticated
 */
export const isAuthenticated = (): boolean => {
  const token = getAuthToken();
  return !!token;
};

/**
 * Get auth token from storage (sessionStorage preferred)
 */
export const getAuthToken = (): string | null => {
  // Check sessionStorage first
  let token = sessionStorage.getItem('authToken');
  
  if (!token) {
    // Check localStorage for backward compatibility
    token = localStorage.getItem('authToken');
    if (token) {
      // Move to sessionStorage and clean localStorage
      sessionStorage.setItem('authToken', token);
      const user = localStorage.getItem('user');
      if (user) {
        sessionStorage.setItem('user', user);
      }
      localStorage.removeItem('authToken');
      localStorage.removeItem('user');
    }
  }
  
  return token;
};

/**
 * Get user data from storage
 */
export const getStoredUser = (): StoredUser | null => {
  try {
    // Check sessionStorage first
    let userStr = sessionStorage.getItem('user');
    
    if (!userStr) {
      // Check localStorage for backward compatibility
      userStr = localStorage.getItem('user');
      if (userStr) {
        // Move to sessionStorage and clean localStorage
        sessionStorage.setItem('user', userStr);
        const token = localStorage.getItem('authToken');
        if (token) {
          sessionStorage.setItem('authToken', token);
        }
        localStorage.removeItem('user');
        localStorage.removeItem('authToken');
      }
    }
    
    return userStr ? JSON.parse(userStr) : null;
  } catch (error) {
    console.error('Error parsing stored user data:', error);
    clearAuthData();
    return null;
  }
};

/**
 * Store auth data in sessionStorage
 */
export const storeAuthData = (token: string, user: any): void => {
  sessionStorage.setItem('authToken', token);
  sessionStorage.setItem('user', JSON.stringify(user));
  
  // Clear any old localStorage data
  localStorage.removeItem('authToken');
  localStorage.removeItem('user');
};

/**
 * Clear all auth data from both storages
 */
export const clearAuthData = (): void => {
  sessionStorage.removeItem('authToken');
  sessionStorage.removeItem('user');
  localStorage.removeItem('authToken');
  localStorage.removeItem('user');
};

/**
 * Check if token is likely expired (basic client-side check)
 */
export const isTokenLikelyExpired = (): boolean => {
  const token = getAuthToken();
  if (!token) return true;
  
  try {
    // Basic JWT structure check
    const parts = token.split('.');
    if (parts.length !== 3) return true;
    
    // Decode payload (basic check, not verifying signature)
    const payload = JSON.parse(atob(parts[1]));
    const now = Math.floor(Date.now() / 1000);
    
    // Check if token is expired (with 5 minute buffer)
    return payload.exp && (payload.exp - 300) < now;
  } catch (error) {
    console.error('Error checking token expiry:', error);
    return true;
  }
};